import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi } from '../utils/api';
import { handleApiError } from '../utils/helpers';

const UserProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    notificationPreferences: {
      email: true,
      push: true
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [formChanged, setFormChanged] = useState(false);
  
  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const profileData = await userApi.getUserProfile();
        setProfile(profileData);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, []);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
    setFormChanged(true);
  };
  
  // Handle checkbox changes for notification preferences
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setProfile(prev => ({
      ...prev,
      notificationPreferences: {
        ...prev.notificationPreferences,
        [name]: checked
      }
    }));
    setFormChanged(true);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      await userApi.updateUserProfile(profile);
      
      setSuccessMessage('Profile updated successfully');
      setFormChanged(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(handleApiError(error));
    } finally {
      setSaving(false);
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    navigate('/');
  };
  
  return (
    <div className="user-profile-page">
      <div className="page-header">
        <h1 className="page-title">My Profile</h1>
        <p className="page-description">
          Manage your account information and preferences
        </p>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="error-message">
          <p>{error}</p>
          <button className="button" onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      )}
      
      {/* Success message */}
      {successMessage && (
        <div className="success-message">
          <p>{successMessage}</p>
        </div>
      )}
      
      {!loading && !error && (
        <div className="profile-container">
          <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-section">
              <h3 className="section-title">Personal Information</h3>
              
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={profile.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profile.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">Phone Number (Optional)</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={profile.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            
            <div className="form-section">
              <h3 className="section-title">Notification Preferences</h3>
              
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="email"
                    checked={profile.notificationPreferences.email}
                    onChange={handleCheckboxChange}
                  />
                  <span>Email Notifications</span>
                </label>
                <p className="checkbox-description">
                  Receive email notifications when your requested cats are fed
                </p>
              </div>
              
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="push"
                    checked={profile.notificationPreferences.push}
                    onChange={handleCheckboxChange}
                  />
                  <span>Push Notifications</span>
                </label>
                <p className="checkbox-description">
                  Receive push notifications on your device when your requested cats are fed
                </p>
              </div>
            </div>
            
            <div className="form-actions">
              <button
                type="submit"
                className="button"
                disabled={saving || !formChanged}
              >
                {saving ? (
                  <>
                    <span className="spinner-small"></span>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
          
          <div className="account-actions">
            <div className="account-section">
              <h3 className="section-title">Account Actions</h3>
              
              <button 
                className="button danger"
                onClick={handleLogout}
              >
                Logout
              </button>
              
              <button 
                className="button secondary"
                onClick={() => navigate('/dashboard/change-password')}
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile; 